import React, {ReactNode} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'

import {cssTheme} from '../../../../themes'
import {ButtonLink} from '../../ButtonLink'
import {Icon} from '../../Icon'
import {Text} from '../../Text'

interface Props {
  /** Pass through classname to allow styles overrides */
  className?: string
  /** The label to be shown next to the badge */
  label?: ReactNode
  /** The sub label shown after the label */
  subLabel?: ReactNode
  /** The number to be shown in the badge */
  value: number
  /** The ID of the Score Rating value */
  scoreId?: string
  /** The ID of the Label text i.e. Superb/Good */
  scoreLabelId?: string
  /** The ID of the SubLabel which is review count  */
  scoreSubLabelId?: string
  /** Show the sub label as plain text, without the separator and the link chevron */
  hasPlainSubLabel?: boolean
  size?: 'md' | 'sm'
}

/**
 * The md badge is the tallest element in the row, so it sets the row height:
 * titleS line-height (20px) plus s200 padding above and below.
 * Exported so loading states can reserve the row height and avoid a shift.
 */
export const SCORE_RATING_SQUARED_HEIGHT = 28

/**
 * The badge is a fixed 32x28 box in the design, so every score renders the same
 * width: a shorter value like "10" or "-" no longer shrinks it.
 */
const BADGE_MIN_WIDTH = 32
const BADGE_MIN_WIDTH_SM = 24

const RatingElement = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  gap: ${cssTheme.layout.spacing.s250};
`

const BadgeWrapper = styled.div`
  ${cssTheme.typography.titleS}
  padding: ${cssTheme.layout.spacing.s200} ${cssTheme.layout.spacing.s250};
  border-radius: ${cssTheme.layout.radius.md};
  text-align: center;
  flex-shrink: 0;
  min-width: ${BADGE_MIN_WIDTH}px;
  box-sizing: border-box;

  .--sm & {
    ${cssTheme.typography.labelXS}
    padding: ${cssTheme.layout.spacing.s100} ${cssTheme.layout.spacing.s200};
    border-radius: ${cssTheme.layout.radius.sm};
    min-width: ${BADGE_MIN_WIDTH_SM}px;
  }

  .--poor & {
    background-color: ${cssTheme.colors.scoreRating.default.neutral.background};
    color: ${cssTheme.colors.scoreRating.default.neutral.score};
  }
  .--high & {
    background-color: ${cssTheme.colors.scoreRating.default.success.background};
    color: ${cssTheme.colors.scoreRating.default.success.score};
  }
`

const LabelWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  gap: ${cssTheme.layout.spacing.s200};
`

const LabelText = styled(Text)`
  color: ${cssTheme.colors.scoreRating.default.description};
`

const Separator = styled.span`
  ${cssTheme.typography.bodyS}
  color: ${cssTheme.colors.scoreRating.default.description};

  .--sm & {
    ${cssTheme.typography.bodyXS}
  }
`

export const ScoreRatingSquared = ({
  label,
  subLabel,
  value,
  className,
  scoreId,
  scoreLabelId,
  scoreSubLabelId,
  hasPlainSubLabel = false,
  size = 'md'
}: Props) => (
  <RatingElement
    className={cx(
      value >= 7 && '--high',
      value < 7 && '--poor',
      size === 'sm' && '--sm',
      className
    )}
  >
    <BadgeWrapper id={scoreId}>{value || '-'}</BadgeWrapper>
    {(label || subLabel) && (
      <LabelWrapper>
        {label && (
          <LabelText
            id={scoreLabelId}
            variant={size === 'sm' ? 'titleXS' : 'titleS'}
            tag="span"
          >
            {label}
          </LabelText>
        )}
        {label && subLabel && !hasPlainSubLabel && (
          <Separator aria-hidden>·</Separator>
        )}
        {subLabel &&
          (hasPlainSubLabel ? (
            <Text
              id={scoreSubLabelId}
              variant={size === 'sm' ? 'bodyXS' : 'bodyS'}
              tag="span"
              colorPath="content.neutral.secondary"
            >
              {subLabel}
            </Text>
          ) : (
            <ButtonLink
              id={scoreSubLabelId}
              size={size === 'sm' ? 'sm' : 'md'}
              isCompact
              // The chevron repeats what the review count already says.
              iconEnd={
                <span aria-hidden>
                  <Icon name="ChevronRight" size="xs" hasMirror />
                </span>
              }
            >
              {subLabel}
            </ButtonLink>
          ))}
      </LabelWrapper>
    )}
  </RatingElement>
)
